import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { HelpCircle, Phone, Mail, Loader2, Send, AlertCircle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_OPTIONS = [
  { value: 'non_classe', label: 'Non classé', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'parapharmacie', label: 'Parapharmacie', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'medicament', label: 'Médicament (sans ordonnance)', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'medicament_prescription', label: 'Médicament à prescription', color: 'bg-red-100 text-red-700 border-red-300' },
];

const CategoryBadge = ({ value }) => {
  const opt = CATEGORY_OPTIONS.find((o) => o.value === value) || CATEGORY_OPTIONS[0];
  return (
    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded border ${opt.color}`}>
      {opt.label}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  if (!status || status === 'not_sent') return null;
  const styles = {
    sent: 'bg-blue-100 text-blue-700 border-blue-300',
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    failed: 'bg-rose-100 text-rose-700 border-rose-300',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-300',
    refunded: 'bg-purple-100 text-purple-700 border-purple-300',
  };
  const labels = {
    sent: 'Lien envoyé',
    paid: 'Payé',
    failed: 'Échec envoi',
    cancelled: 'Paiement annulé',
    refunded: 'Remboursé',
  };
  return (
    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded border ${styles[status]}`}>
      {labels[status] || status}
    </span>
  );
};

const ProductRequestsTab = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [productRequests, setProductRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState({});
  // Payment-link dialog state
  const [paymentDialog, setPaymentDialog] = useState(null);
  // { request, method, amount, preview, step: 'edit'|'preview', sending }

  const fetchProductRequests = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pharmacy/product-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProductRequests(response.data);
    } catch (error) {
      console.error('Error fetching product requests:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProductRequests();
  }, [fetchProductRequests]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const updateStatus = async (requestId, newStatus) => {
    try {
      const notes = adminNotes[requestId] || '';
      const params = new URLSearchParams({ status: newStatus, admin_notes: notes });
      await axios.patch(
        `${API_URL}/pharmacy/product-requests/${requestId}?${params.toString()}`,
        {},
        authHeaders,
      );
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      fetchProductRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updateCategoryType = async (requestId, categoryType) => {
    try {
      await axios.patch(
        `${API_URL}/pharmacy/product-requests/${requestId}/category-type?category_type=${categoryType}`,
        {},
        authHeaders,
      );
      fetchProductRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updatePaymentStatus = async (requestId, paymentStatus) => {
    try {
      await axios.post(
        `${API_URL}/pharmacy/product-requests/${requestId}/payment-status?payment_status=${paymentStatus}`,
        {},
        authHeaders,
      );
      toast.success('Statut paiement mis à jour');
      fetchProductRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updatePickupStatus = async (requestId, pickupStatus) => {
    try {
      await axios.post(
        `${API_URL}/pharmacy/product-requests/${requestId}/pickup-status?pickup_status=${pickupStatus}`,
        {},
        authHeaders,
      );
      toast.success('Retrait mis à jour');
      fetchProductRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Payment link workflow: open → fetch preview → confirm → send
  const openPaymentDialog = (request) => {
    setPaymentDialog({
      request,
      method: 'wave',
      amount: '',
      preview: '',
      step: 'edit',
      sending: false,
    });
  };

  const fetchPreview = async () => {
    if (!paymentDialog) return;
    try {
      const body = {
        method: paymentDialog.method,
        amount: paymentDialog.amount ? Number(paymentDialog.amount) : null,
        confirm: false,
      };
      const res = await axios.post(
        `${API_URL}/pharmacy/product-requests/${paymentDialog.request.id}/payment-link/preview`,
        body,
        authHeaders,
      );
      setPaymentDialog({ ...paymentDialog, preview: res.data.preview, step: 'preview' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aperçu impossible');
    }
  };

  const confirmAndSend = async () => {
    if (!paymentDialog) return;
    setPaymentDialog({ ...paymentDialog, sending: true });
    try {
      const body = {
        method: paymentDialog.method,
        amount: paymentDialog.amount ? Number(paymentDialog.amount) : null,
        confirm: true,
      };
      const res = await axios.post(
        `${API_URL}/pharmacy/product-requests/${paymentDialog.request.id}/payment-link/send`,
        body,
        authHeaders,
      );
      if (res.data.ok) {
        toast.success('SMS de paiement envoyé au client');
      } else {
        toast.warning(`SMS non envoyé (statut: ${res.data.sms_status})`);
      }
      setPaymentDialog(null);
      fetchProductRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'envoi");
      setPaymentDialog({ ...paymentDialog, sending: false });
    }
  };

  // Eligibility for the "Send payment link" button:
  //   - request validated by team (validated_by present)
  //   - status = completed (product is ready for pickup)
  //   - product classified as parapharmacie
  //   - phone present (≥ 8 chars)
  //   - link not already sent (or last send failed)
  const canSendPaymentLink = (req) => {
    const phone = (req.phone || '').replace(/\s+/g, '');
    return (
      req.product_category_type === 'parapharmacie' &&
      req.status === 'completed' &&
      !!req.validated_by &&
      phone.length >= 8 &&
      req.payment_status !== 'sent' &&
      req.payment_status !== 'paid'
    );
  };

  const getStatusInfo = (status) => {
    const styles = {
      pending: { bg: 'border-l-orange-500 bg-orange-50', badge: 'bg-orange-200 text-orange-800' },
      processing: { bg: 'border-l-blue-500 bg-blue-50', badge: 'bg-blue-200 text-blue-800' },
      completed: { bg: 'border-l-green-500 bg-green-50', badge: 'bg-green-200 text-green-800' },
      cancelled: { bg: 'border-l-gray-500 bg-gray-50', badge: 'bg-gray-200 text-gray-800' },
    };
    const labels = {
      pending: 'Demande reçue',
      processing: 'En cours de vérification',
      completed: 'Produit prêt pour retrait',
      cancelled: 'Refusé / Non disponible',
    };
    return { ...(styles[status] || styles.pending), label: labels[status] || status };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Demandes de mise à disposition
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : productRequests.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune demande pour le moment</p>
          ) : (
            <div className="space-y-4">
              {productRequests.map((request) => {
                const statusInfo = getStatusInfo(request.status);
                const eligible = canSendPaymentLink(request);
                return (
                  <div
                    key={request.id}
                    className={`border-l-4 ${statusInfo.bg} rounded-lg p-4`}
                    data-testid={`request-${request.id}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${statusInfo.badge}`}>
                            {statusInfo.label}
                          </span>
                          <span className="text-sm text-gray-500">#{request.id?.slice(-6)}</span>
                          <CategoryBadge value={request.product_category_type} />
                          <PaymentStatusBadge status={request.payment_status} />
                          {request.pickup_status === 'picked_up' && (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded border bg-green-100 text-green-700 border-green-300">
                              Retiré
                            </span>
                          )}
                        </div>

                        <h4 className="font-semibold text-gray-900">
                          {request.requester_name}
                        </h4>
                        <div className="text-sm text-gray-600 flex flex-wrap gap-3 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {request.phone}
                          </span>
                          {request.email && (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {request.email}
                            </span>
                          )}
                        </div>

                        <p className="mt-2 text-sm">
                          <span className="font-medium">Produit demandé :</span>{' '}
                          {request.product_name}
                        </p>
                        {request.description && (
                          <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                        )}

                        {/* Audit log summary */}
                        {(request.validated_at || request.payment_link_sent_at) && (
                          <div className="mt-2 text-[11px] text-gray-500 space-y-0.5">
                            {request.validated_at && (
                              <p>
                                ✓ Validée par {request.validated_by} le{' '}
                                {new Date(request.validated_at).toLocaleString('fr-FR')}
                              </p>
                            )}
                            {request.payment_link_sent_at && (
                              <p>
                                ✓ Lien {request.payment_link_method} envoyé par{' '}
                                {request.payment_link_sent_by} le{' '}
                                {new Date(request.payment_link_sent_at).toLocaleString('fr-FR')}
                                {request.payment_link_amount &&
                                  ` (${Number(request.payment_link_amount).toLocaleString('fr-FR')} FCFA)`}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Category classification (always editable, regulatory critical) */}
                        <div className="mt-3 flex items-center gap-2">
                          <Label className="text-xs text-gray-700 shrink-0">
                            Classification :
                          </Label>
                          <Select
                            value={request.product_category_type || 'non_classe'}
                            onValueChange={(v) => updateCategoryType(request.id, v)}
                          >
                            <SelectTrigger
                              className="h-8 text-xs w-64"
                              data-testid={`category-select-${request.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORY_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value} className="text-xs">
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Textarea
                          placeholder="Notes internes (visibles uniquement par l'équipe)"
                          value={adminNotes[request.id] ?? request.admin_notes ?? ''}
                          onChange={(e) =>
                            setAdminNotes({ ...adminNotes, [request.id]: e.target.value })
                          }
                          className="mt-2 text-xs"
                          rows={1}
                        />
                      </div>

                      {/* Right column: actions */}
                      <div className="flex flex-col gap-2 items-stretch w-48 shrink-0">
                        {request.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateStatus(request.id, 'processing')}
                              data-testid="btn-verify"
                            >
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              Vérifier
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-700 border-rose-300 hover:bg-rose-50"
                              onClick={() => updateStatus(request.id, 'cancelled')}
                            >
                              Refuser
                            </Button>
                          </>
                        )}
                        {request.status === 'processing' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => updateStatus(request.id, 'completed')}
                              data-testid="btn-ready"
                            >
                              Prêt pour retrait
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-700 border-rose-300 hover:bg-rose-50"
                              onClick={() => updateStatus(request.id, 'cancelled')}
                            >
                              Refuser
                            </Button>
                          </>
                        )}
                        {request.status === 'completed' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-[#C45C26] hover:bg-[#A84B1F] disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!eligible}
                              onClick={() => openPaymentDialog(request)}
                              data-testid="btn-send-payment-link"
                              title={
                                eligible
                                  ? "Envoyer un lien de paiement à distance au client"
                                  : "Disponible uniquement pour parapharmacie validée et prête. Le paiement à distance est interdit pour les médicaments."
                              }
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Envoyer lien paiement
                            </Button>
                            {!eligible && (
                              <p className="text-[10px] text-gray-500 leading-tight px-1">
                                {request.product_category_type !== 'parapharmacie'
                                  ? '⚠️ Réservé à la parapharmacie'
                                  : !request.validated_by
                                  ? 'Non validé'
                                  : 'Téléphone invalide'}
                              </p>
                            )}
                            {request.payment_status === 'sent' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updatePaymentStatus(request.id, 'paid')}
                              >
                                Marquer payé
                              </Button>
                            )}
                            {request.pickup_status !== 'picked_up' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updatePickupStatus(request.id, 'picked_up')}
                              >
                                Marquer retiré
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment-link dialog (preview → confirm → send) */}
      <Dialog
        open={!!paymentDialog}
        onOpenChange={(open) => !open && setPaymentDialog(null)}
      >
        <DialogContent className="sm:max-w-lg" data-testid="payment-link-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer un lien de paiement à distance</DialogTitle>
            <DialogDescription>
              Réservé à la parapharmacie. Vérifiez l'aperçu du SMS avant l'envoi.
            </DialogDescription>
          </DialogHeader>

          {paymentDialog && paymentDialog.step === 'edit' && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                <span className="font-medium">{paymentDialog.request.requester_name}</span>{' '}
                <span className="text-gray-500">— {paymentDialog.request.product_name}</span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentDialog({ ...paymentDialog, method: 'wave' })}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    paymentDialog.method === 'wave'
                      ? 'border-[#1DD1A1] bg-[#1DD1A1]/10'
                      : 'border-gray-200'
                  }`}
                  data-testid="payment-method-wave"
                >
                  <span className="w-7 h-7 rounded-md bg-[#1DD1A1] text-white inline-flex items-center justify-center text-xs font-bold">
                    W
                  </span>
                  <span className="font-medium text-sm">Wave</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentDialog({ ...paymentDialog, method: 'orange_money' })}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    paymentDialog.method === 'orange_money'
                      ? 'border-[#FF6B00] bg-[#FF6B00]/10'
                      : 'border-gray-200'
                  }`}
                  data-testid="payment-method-om"
                >
                  <span className="w-7 h-7 rounded-md bg-[#FF6B00] text-white inline-flex items-center justify-center text-[10px] font-bold">
                    OM
                  </span>
                  <span className="font-medium text-sm">Orange Money</span>
                </button>
              </div>

              <div>
                <Label htmlFor="payment-amount">Montant (FCFA) — facultatif</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  min="0"
                  step="100"
                  placeholder="Ex: 12500"
                  value={paymentDialog.amount}
                  onChange={(e) =>
                    setPaymentDialog({ ...paymentDialog, amount: e.target.value })
                  }
                  data-testid="payment-amount-input"
                />
                {paymentDialog.amount && Number(paymentDialog.amount) > 0 && (
                  <p className="text-xs font-medium mt-1 text-gray-700">
                    {Math.round(Number(paymentDialog.amount)).toLocaleString('fr-FR')} FCFA
                  </p>
                )}
              </div>
            </div>
          )}

          {paymentDialog && paymentDialog.step === 'preview' && (
            <div className="space-y-3 py-2">
              <div className="text-xs text-gray-500">Aperçu du SMS qui sera envoyé au :</div>
              <div className="text-sm font-mono text-gray-700">{paymentDialog.request.phone}</div>
              <div
                className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 whitespace-pre-wrap leading-relaxed"
                data-testid="sms-preview-content"
              >
                {paymentDialog.preview}
              </div>
              <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border rounded-lg p-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  En confirmant, l'envoi est définitif et tracé dans l'historique
                  (envoyeur, date, méthode, montant).
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {paymentDialog && paymentDialog.step === 'edit' && (
              <>
                <Button variant="outline" onClick={() => setPaymentDialog(null)}>
                  Annuler
                </Button>
                <Button onClick={fetchPreview} data-testid="btn-payment-preview">
                  Aperçu du SMS
                </Button>
              </>
            )}
            {paymentDialog && paymentDialog.step === 'preview' && (
              <>
                <Button
                  variant="outline"
                  onClick={() =>
                    setPaymentDialog({ ...paymentDialog, step: 'edit' })
                  }
                  disabled={paymentDialog.sending}
                >
                  Modifier
                </Button>
                <Button
                  onClick={confirmAndSend}
                  disabled={paymentDialog.sending}
                  className="bg-[#C45C26] hover:bg-[#A84B1F]"
                  data-testid="btn-payment-confirm"
                >
                  {paymentDialog.sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirmer l'envoi
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductRequestsTab;
